import { useApi } from "@/api";
import { ContentType, TCurrency } from "@/api/v1_0";
import { RootStore } from "@/store/root";
import { IHistory, ISummary } from "@/types";
import endOfMonth from "date-fns/endOfMonth";
import format from "date-fns/format";
import startOfMonth from "date-fns/startOfMonth";
import { makeAutoObservable } from "mobx";

export class DashboardStore {
  private _rootStore: RootStore;

  public loading = false;
  public refreshing = false;

  public summary: ISummary = {
    income: 0,
    outcome: 0,
    balance: 0,
    debts: 0,
  };

  public baseCurrency: TCurrency = TCurrency.RUB;
  public date: Date = new Date();
  public history: { values: Array<IHistory> } | undefined = undefined;

  constructor(rootStore: RootStore) {
    this._rootStore = rootStore;

    makeAutoObservable(this);
  }

  public toggleCurrency = async (): Promise<void> => {
    switch (this.baseCurrency) {
      case TCurrency.EUR:
        this.baseCurrency = TCurrency.RUB;

        break;
      case TCurrency.RUB:
        this.baseCurrency = TCurrency.USD;

        break;
      case TCurrency.USD:
        this.baseCurrency = TCurrency.EUR;

        break;
    }

    await this.load(this.baseCurrency);
  };

  public load = async (
    currency: TCurrency | undefined = undefined
  ): Promise<void> => {
    this._rootStore.logging.track("dashboard.load", {
      currency: currency || this.baseCurrency,
    });

    try {
      this.loading = true;

      const api = useApi();

      const {
        data: { income, outcome, balance, debts },
      } = await api.v10.appHttpApiV10ControllerWalletSummaryDashboardRead(
        {
          currency: currency || this.baseCurrency,
          end: format(endOfMonth(this.date), "yyyy-MM-dd"),
          precision: 2,
          start: format(startOfMonth(this.date), "yyyy-MM-dd"),
        },
        {
          headers: {
            "Content-Type": ContentType.Json,
          },
        }
      );

      this.summary = {
        income,
        outcome,
        balance,
        debts,
      };
    } catch (err) {
      throw this._rootStore.logging.capture(err);
    } finally {
      this.loading = false;
    }
  };

  public refresh = async (
    currency: TCurrency | undefined = undefined
  ): Promise<void> => {
    try {
      this.refreshing = true;

      await this.load();
    } finally {
      this.refreshing = false;
    }
  };
}
